import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { CreateSectionDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  planGuid: string;
  guid: string;
  blockGuid: string;
  name: string;
  hintText: string;
  sortOrder: string;
  createdByUserId: string;
};

const useCreateSectionRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createSectionRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateSectionDto = {
      guid: variables.guid,
      blockGuid: variables.blockGuid,
      name: variables.name,
      hintText: variables.hintText,
      sortOrder: variables.sortOrder,
      createdByUserId: variables.createdByUserId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/sections`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createSectionRequest };
};

const useCreateSection = () => {
  const { createSectionRequest } = useCreateSectionRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createSectionRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useCreateSection };
